import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { dropdownGroupsDashboard } from "../../Constant/ConstantData";

const MultiLineRegistrationChart = ({ registerData, userData }) => {
  const colors = {
    year3: "#c900a4",
    year4: "#6c35db",
    year5: "#249a95",
    year6: "#f8ad4d",
    year7: "#fcd46b",
    year8: "#fb8acb",
    year9: "#be00ab",
    year10: "#160071",
    year11: "#c900a4",
    year14: "#6c35db",
    parent: "#249a95",
  };

  // Get all unique year keys from the data and sort them
  const yearKeys = registerData
    ? [
        ...new Set(
          registerData.flatMap((item) =>
            Object.keys(item).filter(
              (key) => key.startsWith("year") || key === "parent"
            )
          )
        ),
      ].sort((a, b) => {
        if (a === "parent") return 1;
        if (b === "parent") return -1;
        const numA = parseInt(a.replace("year", ""));
        const numB = parseInt(b.replace("year", ""));
        return numA - numB;
      })
    : [];

  // Process data to ensure all years have values for every date and make it cumulative
  const processedData = useMemo(() => {
    if (!registerData || registerData.length === 0) return [];

    // First, ensure all years have values for every date
    const dataWithAllYears = registerData.map((item) => {
      const processedItem = { ...item };
      yearKeys.forEach((yearKey) => {
        if (!(yearKey in processedItem)) {
          processedItem[yearKey] = 0;
        }
      });
      return processedItem;
    });

    // Then make the data cumulative
    const cumulativeData = [];
    const runningTotals = {};

    // Initialize running totals
    yearKeys.forEach((yearKey) => {
      runningTotals[yearKey] = 0;
    });

    dataWithAllYears.forEach((item) => {
      const cumulativeItem = { ...item };

      yearKeys.forEach((yearKey) => {
        runningTotals[yearKey] += item[yearKey] || 0;
        cumulativeItem[yearKey] = runningTotals[yearKey];
      });

      cumulativeData.push(cumulativeItem);
    });

    return cumulativeData;
  }, [registerData, yearKeys]);

  const shouldScroll = processedData?.length > 10;

  return (
    <div
      style={{
        width: "100%", // Match parent Col width
        maxWidth: "100%", // Prevent overflow beyond Col
        overflowX: shouldScroll ? "auto" : "visible", // Enable horizontal scroll when needed
        overflowY: "hidden", // Prevent vertical scroll
        boxSizing: "border-box", // Include padding/margins in width
        scrollbarWidth: "auto", // Ensure scrollbar is visible (Firefox)
        msOverflowStyle: "auto", // Ensure scrollbar is visible (IE/Edge)
      }}
      className="chart-scroll-container" // Add class for additional CSS
    >
      <div
        style={{
          width: shouldScroll ? `${processedData.length * 80}px` : "100%", // Increased multiplier for scroll
          minWidth: "100%", // Ensure content fills parent when not scrolling
          boxSizing: "border-box", // Prevent padding/margins from causing overflow
        }}
      >
        <ResponsiveContainer height={300}>
          <LineChart
            data={processedData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
            />
            <YAxis />
            <Tooltip
              content={({ payload }) => {
                if (payload && payload.length > 0) {
                  const filteredPayload = payload.filter(
                    (item) => item.value !== 0
                  );
                  if (filteredPayload.length === 0) return null;

                  const total = filteredPayload.reduce(
                    (sum, item) => sum + item.value,
                    0
                  );

                  return (
                    <div
                      style={{
                        backgroundColor: "white",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      {filteredPayload.map((item, index) => (
                        <div key={index} style={{ color: item.color }}>
                          {item.name}: {item.value}
                        </div>
                      ))}
                      <div
                        style={{
                          borderTop: "1px solid #ccc",
                          marginTop: "4px",
                          paddingTop: "4px",
                        }}
                      >
                        Total: {total}
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            {/* <Legend
              align="center"
              wrapperStyle={{
                marginLeft: "30px",
              }}
            /> */}
            <Legend
              content={() => {
                const legendItems = [];

                dropdownGroupsDashboard
                  .filter((group) =>
                    userData?.schoolGroup?.includes(group.group)
                  )
                  .forEach((group) => {
                    group.years.forEach((year) => {
                      const yearKey = `year${year.value}`;
                      legendItems.push({
                        key: yearKey,
                        name: year.name,
                        color: colors[yearKey] || "#666",
                      });
                    });
                  });

                legendItems.push({
                  key: "parent",
                  name: "Parent",
                  color: colors.parent,
                });

                return (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: "15px",
                      marginTop: "10px",
                    }}
                  >
                    {legendItems.map((item) => (
                      <div
                        key={item.key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        {item.name !== "Parent" && (
                          <>
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                backgroundColor: item.color,
                                borderRadius: "2px",
                              }}
                            ></div>

                            <span style={{ fontSize: "12px", color: "#666" }}>
                              {"Year-" + item.name}
                            </span>
                          </>
                        )}
                        {/* <span style={{ fontSize: "12px", color: "#666" }}>
                          {item.name === "Parent"
                            ? "Parent"
                            : "Year-" + item.name}
                        </span> */}
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            {yearKeys.map((yearKey) => {
              const displayName =
                yearKey === "parent"
                  ? "Parent"
                  : `Year ${yearKey.replace("year", "")}`;
              return (
                <Line
                  key={yearKey}
                  type="monotone"
                  dataKey={yearKey}
                  stroke={colors[yearKey] || "#666"}
                  name={displayName}
                  dot={{ r: 3 }}
                  strokeWidth={2}
                  connectNulls={false}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* // commented this code  */}

      {/* <style>{`
  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
  }
  .col-xxl-7,
  .col-xxl-5 {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .cmn-card-color.table-cell {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
  .details-chart {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }
  .chart-scroll-container {
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: auto;
    scrollbar-color: #888 #f1f1f1;
  }
  .chart-scroll-container::-webkit-scrollbar {
    height: 12px;
  }
  .chart-scroll-container::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  .chart-scroll-container::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 6px;
  }
  .chart-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`}</style> */}
    </div>
  );
};

export default MultiLineRegistrationChart;
